import { useReducedMotion } from 'framer-motion'
import type { ReportView, VersionInfo } from '@/lib/types/report'
import { mergeOutline, outlineProgress } from '@/lib/report/mergeOutline'
import { lastWriteVersions } from '@/lib/report/versionDiff'
import { useFollowLive } from '@/hooks/report/useFollowLive'
import { cn } from '@/lib/utils'
import { ReportShell } from './ReportShell'
import { ReportRenderer } from './ReportRenderer'
import { VersionTimeline } from './VersionTimeline'
import { ActivityFeed } from './ActivityFeed'

interface ReportViewerProps {
  view: ReportView
  versions: VersionInfo[]
  selected: number
  headVersion: number
  isLive: boolean
  writingSection?: string | null
  changedIds: string[]
  contentVersion: number
  onSelectVersion: (version: number) => void
}

/** The one report viewer — mounted on /reports/:id, /r/:token, and the workspace panel.
 *  Purely presentational: data, polling, and sockets live in the mount's page/hook. */
export function ReportViewer({
  view, versions, selected, headVersion, isLive, writingSection,
  changedIds, contentVersion, onSelectVersion,
}: ReportViewerProps) {
  const reduceMotion = useReducedMotion()
  const { following, hasNewUpdate, resume, pause } = useFollowLive({
    isLive, contentVersion, changedIds, reduceMotion,
  })
  const blocks = mergeOutline(view.blocks, view.outline, view.status)
  const progress = outlineProgress(view.outline)
  const sectionVersions = lastWriteVersions(versions, contentVersion)
  const writingTitle =
    writingSection != null
      ? (view.outline.find((o) => o.id === writingSection)?.title
        ?? view.blocks.find((b) => b.id === writingSection)?.title
        ?? writingSection)
      : null
  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="print:hidden sticky top-0 z-10 mb-4 flex flex-wrap items-center justify-between gap-3 bg-background/85 px-2 py-2 backdrop-blur">
        <VersionTimeline
          versions={versions} outline={view.outline} selected={selected} headVersion={headVersion}
          isLive={isLive} onSelect={onSelectVersion}
        />
        <div className="flex items-center gap-3">
          {writingTitle && (
            <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--report-accent)]" aria-hidden="true" />
              Agent is writing <span className="font-medium text-foreground">{writingTitle}</span>…
            </div>
          )}
          {isLive && progress.total > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Report progress">
              <span className="tabular-nums">
                {progress.written} / {progress.total} sections
              </span>
              <span className="h-1 w-20 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                <span
                  className="block h-full rounded-full bg-[var(--report-accent)] transition-[width] duration-500"
                  style={{ width: `${(progress.written / progress.total) * 100}%` }}
                />
              </span>
            </div>
          )}
          {isLive && (
            <button
              type="button"
              onClick={following ? pause : resume}
              aria-pressed={following}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                following
                  ? 'border-[var(--report-accent)] bg-[var(--report-accent)]/10 text-[var(--report-accent)]'
                  : 'border-border text-muted-foreground hover:border-[var(--report-accent)]/50',
              )}
            >
              {following ? '● Following' : 'Follow live'}
            </button>
          )}
          <ActivityFeed versions={versions} outline={view.outline} selected={selected} onSelect={onSelectVersion} />
        </div>
      </div>
      <ReportShell
        title={view.title}
        metadata={{ method: view.facts.method_name ? String(view.facts.method_name.value) : undefined }}
        sections={
          <ReportRenderer
            blocks={blocks} changedIds={changedIds} contentVersion={contentVersion}
            writingId={isLive ? (writingSection ?? null) : null}
            sectionVersions={sectionVersions} showChips={isLive}
          />
        }
      />
      {hasNewUpdate && (
        <button
          type="button"
          onClick={resume}
          className="print:hidden fixed bottom-6 left-1/2 z-20 -translate-x-1/2 rounded-full border border-[var(--report-accent)]/50 bg-background/95 px-4 py-1.5 text-sm font-medium text-[var(--report-accent)] shadow-lg backdrop-blur transition-colors hover:bg-[var(--report-accent)]/10"
        >
          New update ↓
        </button>
      )}
    </div>
  )
}
